import React, { useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import FileService from '@/modules/file/fileService'
import { DATEV_ERROR_DUPLICATE_FILE } from '@/modules/document-export/constants'
import { ButtonIconSquare } from '@/modules/base/components/buttons/index'
import FilenameInput from '@/modules/base/components/inputs/text-input/filename-input'
import { splitFilename } from '@/modules/_helpers/splitFilename'
import { modalConfirmation } from '@/modules/_helpers/openModal'
import { toast } from '@/modules/base/components/toast/toastConfig'
import customDecodeUri from '@/utils/customDecodeUri'
import customEncodeUri from '@/utils/customEncodeUri'
import { FailedDocument, FileInfoEntry } from './types'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import IconDelete from '@assets/images/icons/trashcan.svg'

const fixErrorsLang = lang.documentExport.fixErrors

interface FixErrorsErrorPanelProps {
  item: FailedDocument
  fileInfo?: FileInfoEntry
  onFileChange: (update: FileInfoEntry) => void
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  category: {
    color: theme.textLight,
    fontSize: 12,
  },
  fileName: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 20,
    wordBreak: 'break-all',
  },
  errorBox: {
    padding: 15,
    border: `1px solid ${theme.error}`,
    borderRadius: 4,
    marginBottom: 20,
  },
  errorCode: {
    fontWeight: 600,
  },
  errorDescription: {
    marginTop: 5,
    wordBreak: 'break-word',
  },
  errorLink: {
    display: 'inline-block',
    marginTop: 5,
    color: theme.primary,
  },
  resolution: {
    fontWeight: 600,
    color: theme.primary,
    marginBottom: 20,
  },
  hint: {
    marginBottom: 20,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
  },
  action: {
    marginBottom: 20,
  },
  actionHint: {
    marginBottom: 10,
  },
  actionCaption: {
    fontWeight: theme.fontWeights.bold,
    marginBottom: 10,
  },
}))

const FixErrorsErrorPanel: React.FC<FixErrorsErrorPanelProps> = ({ item, fileInfo = {}, onFileChange }) => {
  const classes = useStyles()
  const [isSaving, setIsSaving] = useState(false)

  const { status, file, resolution } = fileInfo
  const { error } = item
  const isDuplicateError = error?.error === DATEV_ERROR_DUPLICATE_FILE
  // File is excluded from DATEV - either flagged in this session or already excluded on load.
  const isExcluded = resolution === 'flagged' || (!resolution && !!file?.excludeFromDatev)
  const showActions = status === 'loaded' && !resolution && !isExcluded
  const fileName = customDecodeUri(file?.name ?? item.name ?? '')

  const onRename = (baseName: string) => {
    if (!baseName) {
      return
    }

    const { suffix } = splitFilename(file.name)
    const name = customEncodeUri(`${baseName}${suffix}`)

    setIsSaving(true)
    FileService.updateFileInfo({ id: file.id, name })
      .then(() => {
        toast.showToast({ message: fixErrorsLang.renameSuccess })
        onFileChange({ file: { ...file, name }, resolution: 'renamed' })
      })
      .catch(() => toast.showToast({ type: 'error', message: fixErrorsLang.renameError }))
      .finally(() => setIsSaving(false))
  }

  const onDelete = () => {
    modalConfirmation(() => {
      setIsSaving(true)
      FileService.deleteFile(file.id)
        .then(() => {
          toast.showToast({ message: fixErrorsLang.deleteSuccess })
          onFileChange({ resolution: 'deleted' })
        })
        .catch((e) => {
          if (e.body.message.includes('file is assigned')) {
            toast.showToast({ type: 'error', message: fixErrorsLang.deleteAssignedError })
          } else {
            toast.showToast({ type: 'error', message: fixErrorsLang.deleteError })
          }
        })
        .finally(() => setIsSaving(false))
    }, {
      title: fixErrorsLang.deleteConfirmTitle,
      description: fixErrorsLang.deleteConfirmDescription(fileName),
    })
  }

  const onFlag = () => {
    setIsSaving(true)
    FileService.excludeFromDatev(file.id, true)
      .then(() => {
        toast.showToast({ message: fixErrorsLang.flagSuccess })
        onFileChange({ resolution: 'flagged' })
      })
      .catch(() => toast.showToast({ type: 'error', message: fixErrorsLang.flagError }))
      .finally(() => setIsSaving(false))
  }

  const onInclude = () => {
    setIsSaving(true)
    FileService.excludeFromDatev(file.id, false)
      .then(() => {
        toast.showToast({ message: fixErrorsLang.includeSuccess })
        onFileChange({ file: { ...file, excludeFromDatev: false }, resolution: undefined })
      })
      .catch(() => toast.showToast({ type: 'error', message: fixErrorsLang.includeError }))
      .finally(() => setIsSaving(false))
  }

  const deleteButton = useMemo(() => {
    if (!isDuplicateError) return null

    const button = (<ButtonIconSquare
      label={fixErrorsLang.deleteButton}
      icon={IconDelete}
      onClick={onDelete}
      color='red'
      disabled={isSaving || fileInfo?.file?.isAssigned}
      dataQsId='datev-fix-errors-delete'
    />)
    return fileInfo?.file?.isAssigned ? <RichTooltip content={fixErrorsLang.deleteAssignedError} placement="top">{button}</RichTooltip> : button
  }, [isDuplicateError, isSaving, fileInfo?.file?.isAssigned])

  return (
    <div>
      <div className={classes.category}>{fixErrorsLang.categories[item.category] ?? item.category}</div>
      <div className={classes.fileName}>{fileName}</div>

      {error && (
        <div className={classes.errorBox}>
          <div className={classes.errorCode}>{fixErrorsLang.errorCodeLabel}: {error.error}</div>
          {error.error_description && (
            <div className={classes.errorDescription}>
              {fixErrorsLang.errorDescriptionLabel}: {error.error_description}
            </div>
          )}
          {error.error_uri && (
            <a href={error.error_uri} target="_blank" rel="noopener noreferrer" className={classes.errorLink}>
              {fixErrorsLang.errorUriLabel}
            </a>
          )}
        </div>
      )}

      {resolution === 'renamed' && <div className={classes.resolution}>{fixErrorsLang.fileRenamed}</div>}
      {resolution === 'deleted' && <div className={classes.resolution}>{fixErrorsLang.fileDeleted}</div>}

      {status === 'loaded' && isExcluded && (
        <div className={classes.actions}>
          <div className={classes.resolution}>{fixErrorsLang.fileExcluded}</div>
          <div className={classes.action}>
            <div className={classes.actionHint}>{fixErrorsLang.alreadyExcludedHint}</div>
            <ButtonIconSquare
              width='100%'
              label={fixErrorsLang.includeButton}
              variant="outline"
              disabled={isSaving}
              onClick={onInclude}
              dataQsId="datev-fix-errors-include"
            />
          </div>
        </div>
      )}

      {showActions && (
        <div className={classes.actions}>
          <div className={classes.hint}>
            {isDuplicateError ? fixErrorsLang.duplicateHint : fixErrorsLang.manualFixHint}
          </div>

          {isDuplicateError && (
            <>
              <div className={classes.action}>
                <div className={classes.actionCaption}>{fixErrorsLang.renameCaption}</div>
                <div className={classes.actionHint}>{fixErrorsLang.renameHint}</div>
                <FilenameInput file={file} onUpdate={onRename} />
              </div>

              <div className={classes.action}>
                <div className={classes.actionCaption}>{fixErrorsLang.deleteCaption}</div>
                <div className={classes.actionHint}>{fixErrorsLang.deleteHint}</div>
                {deleteButton}
                {fileInfo?.file?.isAssigned && (fileInfo?.file?.expense?.id || fileInfo?.file?.revenue?.id) && (
                  <a href={fileInfo?.file?.expense?.id ? `/expenses/edit/${fileInfo?.file?.expense?.id}` : `/revenue/edit/${fileInfo?.file?.revenue?.id}`} target="_blank" rel="noopener noreferrer" className={classes.errorLink}>
                    {fileInfo?.file?.expense?.id ? fixErrorsLang.openExpenseInNewTab : fixErrorsLang.openRevenueInNewTab}
                  </a>
                )}
              </div>
            </>
          )}

          <div className={classes.action}>
            <div className={classes.actionCaption}>{fixErrorsLang.flagCaption}</div>
            <div className={classes.actionHint}>{fixErrorsLang.flagHint}</div>
            <ButtonIconSquare
              width='100%'
              label={fixErrorsLang.flagButton}
              variant="outline"
              disabled={isSaving}
              onClick={onFlag}
              dataQsId="datev-fix-errors-flag"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default FixErrorsErrorPanel
